import moment from "moment";
import { parseCardType } from "./cardTypes";
import { translate } from "../i18n/messages";

// Links are often written by an AI assistant rather than built in the app,
// and the canonical format (UTC ISO start time plus a percent-encoded JSON
// agenda) is easy to get slightly wrong by hand. This module accepts the
// looser forms documented in public/llms.txt and rewrites them into the
// canonical starttime/agenda parameters the rest of the app reads.

const DEFAULT_DURATION = "00:10:00";

function pad(value) {
  return String(value).padStart(2, "0");
}

function secondsToHHmmss(totalSeconds) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

const UNIT_SECONDS = [
  [/^(h|hr|hrs|hours?|std|stunden?)$/, 3600],
  [/^(m|min|mins|minutes?|minuten?)$/, 60],
  [/^(s|sec|secs|seconds?|sek|sekunden?)$/, 1],
];

// "1h30", "1h 30m", "90m", "45s", "1 Std. 30 Min.": number/unit pairs. A
// number without a unit is only allowed right after hours ("1h30") and
// means minutes.
function parseUnitDuration(text) {
  const parts = [...text.matchAll(/(\d+(?:\.\d+)?)\s*([a-z]*)\.?\s*/g)];
  if (!parts.length || parts.map((part) => part[0]).join("") !== text) {
    return null;
  }
  let seconds = 0;
  let previousUnit = null;
  for (const [, number, unit] of parts) {
    let factor = UNIT_SECONDS.find(([pattern]) => pattern.test(unit))?.[1];
    if (!unit && previousUnit === 3600) {
      factor = 60;
    }
    if (!factor) {
      return null;
    }
    seconds += +number * factor;
    previousUnit = factor;
  }
  return Math.round(seconds);
}

// Reads a card duration and returns it as "HH:MM:SS", or null if it can't
// be read. A bare number means minutes; "1h30", "90m", "45s", "1:30"
// (hours:minutes) and "00:15:00" are understood as well.
export function parseDuration(value) {
  if (typeof value === "number") {
    return value > 0 ? secondsToHHmmss(Math.round(value * 60)) : null;
  }
  if (typeof value !== "string") {
    return null;
  }
  const text = value
    .trim()
    .toLowerCase()
    .replace(",", ".")
    .replace(/\.$/, "");

  let seconds = null;
  let match;
  if ((match = text.match(/^(\d+):(\d{1,2}):(\d{1,2})$/))) {
    seconds = +match[1] * 3600 + +match[2] * 60 + +match[3];
  } else if ((match = text.match(/^(\d+):(\d{1,2})$/))) {
    seconds = +match[1] * 3600 + +match[2] * 60;
  } else if ((match = text.match(/^(\d+(?:\.\d+)?)$/))) {
    seconds = Math.round(+match[1] * 60);
  } else {
    seconds = parseUnitDuration(text);
  }

  return seconds > 0 ? secondsToHHmmss(seconds) : null;
}

// German dates as an assistant writing in German tends to put them.
const GERMAN_DATE_FORMATS = [
  "D.M.YYYY H:mm",
  "D.M.YYYY, H:mm",
  "D.M.YYYY H:mm [Uhr]",
  "D.M.YYYY, H:mm [Uhr]",
  "D.M.YYYY",
];

// Reads a start time and returns it as a moment, or null. "now", a clock
// time ("09:00", "9.30", "9 Uhr", "9:30am") meaning today in the browser's
// time zone, an ISO date-time without offset (local time) and one with "Z"
// or an offset (absolute) are all understood, as well as a German date
// ("20.10.2026 09:00").
export function parseStart(value, now = moment()) {
  if (typeof value !== "string") {
    return null;
  }
  const text = value.trim();
  if (text.toLowerCase() === "now") {
    return now.clone();
  }

  const clock = text.match(
    /^(\d{1,2})(?:[:.](\d{2}))?(?:[:.](\d{2}))?\s*(uhr|h|am|pm)?$/i
  );
  if (clock) {
    let [hours, minutes, seconds] = [+clock[1], +clock[2] || 0, +clock[3] || 0];
    const suffix = clock[4]?.toLowerCase();
    if (suffix === "am" || suffix === "pm") {
      if (hours < 1 || hours > 12) {
        return null;
      }
      hours = (hours % 12) + (suffix === "pm" ? 12 : 0);
    }
    if (hours > 23 || minutes > 59 || seconds > 59) {
      return null;
    }
    return now
      .clone()
      .set({ hour: hours, minute: minutes, second: seconds, millisecond: 0 });
  }

  // "2026-10-20 09:00" is ISO 8601 with a space instead of the "T".
  const iso = moment(
    text.replace(/^(\d{4}-\d{2}-\d{2}) (?=\d)/, "$1T"),
    moment.ISO_8601,
    true
  );
  if (iso.isValid()) {
    return iso;
  }
  const german = moment(text, GERMAN_DATE_FORMATS, true);
  return german.isValid() ? german : null;
}

// Characters that chat windows, Markdown and e-mail glue to the end of a
// link: punctuation after it ("...~60)."), formatting around it ("**",
// "`", quotes, ">"). A closing parenthesis is only cut when it has no
// opening one inside the value, so "note:Snacks+(optional)" stays intact.
const TRAILING_JUNK = /[\s*_`"'“”‘’»>\].,;:!]+$/;

function count(text, char) {
  return text.split(char).length - 1;
}

export function stripTrailingJunk(text) {
  let result = text;
  let previous;
  do {
    previous = result;
    result = result.replace(TRAILING_JUNK, "");
    if (result.endsWith(")") && count(result, "(") < count(result, ")")) {
      result = result.slice(0, -1);
    }
  } while (result !== previous);
  return result;
}

// Markdown escapes ("\|", "\~", "\_"), look-alike separators from other
// keyboards and fonts, and line breaks between cards (an assistant that
// wraps a long link) are read as what they stand for.
function cleanPlan(plan) {
  return stripTrailingJunk(
    plan
      .replace(/\\([|~_*[\]()#&])/g, "$1")
      .replace(/[｜¦│]/g, "|")
      .replace(/[～〜]/g, "~")
      .replace(/\s*[\r\n]+\s*/g, "|")
  );
}

const PLAN_FIELDS = {
  type: "type",
  typ: "type",
  owner: "owner",
  who: "owner",
  by: "owner",
  wer: "owner",
  note: "note",
  notes: "note",
  notiz: "note",
};

// "Welcome~10|Brainstorming~25~type:work~owner:Anna~note:Bring post-its":
// cards separated by "|", each a topic followed by "~"-separated
// parts - the duration, and optional key:value details. (Not ";" as well:
// that is common inside notes written by an assistant.)
function parsePlan(plan, warn) {
  return plan
    .split("|")
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const [topic, ...parts] = entry.split("~").map((part) => part.trim());
      const card = { topic };
      parts.filter(Boolean).forEach((part) => {
        const field = part.match(/^([a-zäöü]+)\s*:\s*(.*)$/i);
        const key = field && PLAN_FIELDS[field[1].toLowerCase()];
        if (key) {
          card[key] = field[2];
        } else if (card.time === undefined) {
          card.time = part;
        } else {
          warn("ignored", { part, topic });
        }
      });
      return card;
    });
}

// Windows-1252 characters for the bytes 0x80-0x9F (the rest of the byte
// range is the same as Latin-1). Unassigned bytes map to themselves.
const CP1252_HIGH =
  "€\x81‚ƒ„…†‡ˆ‰Š‹Œ\x8dŽ\x8f\x90‘’“”•–—˜™š›œ\x9džŸ";

function byteToCp1252(byte) {
  return byte >= 0x80 && byte <= 0x9f
    ? CP1252_HIGH[byte - 0x80]
    : String.fromCharCode(byte);
}

function cp1252ToByte(char) {
  const index = CP1252_HIGH.indexOf(char);
  if (index >= 0) {
    return 0x80 + index;
  }
  const code = char.charCodeAt(0);
  return code <= 0xff ? code : null;
}

function utf8Length(lead) {
  if (lead >= 0xc2 && lead <= 0xdf) return 2;
  if (lead >= 0xe0 && lead <= 0xef) return 3;
  if (lead >= 0xf0 && lead <= 0xf4) return 4;
  return 0;
}

// Decodes bytes as UTF-8; a byte that isn't part of a valid UTF-8 sequence
// is read as Windows-1252 instead (so "%DF" still becomes "ß").
function decodeBytes(bytes) {
  let text = "";
  for (let i = 0; i < bytes.length; ) {
    const length = bytes[i] < 0x80 ? 1 : utf8Length(bytes[i]);
    let decoded = null;
    if (length && i + length <= bytes.length) {
      try {
        decoded = decodeURIComponent(
          bytes
            .slice(i, i + length)
            .map((byte) => `%${byte.toString(16).padStart(2, "0")}`)
            .join("")
        );
      } catch (error) {
        decoded = null;
      }
    }
    if (decoded === null) {
      text += byteToCp1252(bytes[i]);
      i += 1;
    } else {
      text += decoded;
      i += length;
    }
  }
  return text;
}

function decodePercentRun(run) {
  return decodeBytes(run.match(/%../g).map((hex) => parseInt(hex.slice(1), 16)));
}

// "Ã¼" for "ü", "ÃŸ" for "ß": UTF-8 that was read as Windows-1252 and
// encoded again. Only runs that turn back into valid UTF-8 are replaced.
function repairMojibake(text) {
  return text.replace(
    /[Â-ô][\u0080-¿€‚ƒ„…†‡ˆ‰Š‹ŒŽ‘’“”•–—˜™š›œžŸ]{1,3}/g,
    (run) => {
      const bytes = [...run].map(cp1252ToByte);
      if (bytes.length !== utf8Length(bytes[0])) {
        return run;
      }
      try {
        return decodeURIComponent(
          bytes.map((byte) => `%${byte.toString(16)}`).join("")
        );
      } catch (error) {
        return run;
      }
    }
  );
}

// Assistants percent-encode umlauts and other special characters by hand
// and often get it wrong: Latin-1 instead of UTF-8 ("Ma%DFnahmen"),
// encoded twice ("Ma%25C3%259Fnahmen") or garbled ("MaÃŸnahmen"). Decodes
// a raw query value like URLSearchParams does, but repairs those mistakes
// instead of turning them into "�".
export function decodeLenient(raw) {
  let text = raw
    .replace(/\+/g, " ")
    .replace(/(%[0-9a-f]{2})+/gi, decodePercentRun);
  // Left-over escapes are a second encoding round, as long as they decode
  // to non-ASCII text ("%C3%9F"), so a literal "%26" in a note stays.
  text = text.replace(/(%[0-9a-f]{2})+/gi, (run) =>
    /%[89a-f]/i.test(run) ? decodePercentRun(run) : run
  );
  return repairMojibake(text);
}

// The raw (still encoded) value of a query parameter, or null.
function rawParam(search, name) {
  const pair = search
    .replace(/^\?/, "")
    .split("&")
    .find((part) => part.split("=")[0] === name);
  return pair === undefined ? null : pair.slice(name.length + 1);
}

function parseJsonArray(text) {
  try {
    const parsed = JSON.parse(text);
    return Array.isArray(parsed) ? parsed : null;
  } catch (error) {
    return null;
  }
}

// Returns { cards, repaired }: the cards of a JSON agenda, or null. Text
// glued to the end of the link after the closing bracket is cut off, and
// repaired says so, because the link then has to be rewritten.
function parseAgendaJson(agenda) {
  const cards = parseJsonArray(agenda);
  if (cards !== null) {
    return { cards, repaired: false };
  }
  const end = agenda.lastIndexOf("]");
  const trimmed = end >= 0 ? parseJsonArray(agenda.slice(0, end + 1)) : null;
  return { cards: trimmed, repaired: trimmed !== null };
}

const KNOWN_KEYS = ["start", "plan", "starttime", "agenda"];

// Links copied out of a chat, an e-mail or an HTML page pick up small
// mistakes before they reach the app: "&amp;" for "&", "Plan=" for
// "plan=", or an unencoded "&" inside a topic ("Q&A") that splits the
// plan in two. Returns the query string (without "?") with those repaired.
export function repairQuery(search) {
  const repaired = [];
  let inPlan = false;
  search
    .replace(/^\?/, "")
    .replace(/&amp;/gi, "&")
    .split("&")
    .forEach((part) => {
      const key = part.match(/^([a-z_][\w-]*)=/i)?.[1];
      if (key === undefined) {
        if (inPlan && part) {
          repaired[repaired.length - 1] += `%26${part}`;
        } else if (part) {
          repaired.push(part);
        }
        return;
      }
      const name = KNOWN_KEYS.includes(key.toLowerCase())
        ? key.toLowerCase()
        : key;
      inPlan = name === "plan";
      repaired.push(name + part.slice(key.length));
    });
  return repaired.join("&");
}

// Brings every card into the {id, topic, time} shape (plus the optional
// owner, note and type): fills in a missing id or topic, accepts
// "title"/"name" for the topic and "duration" or "minutes" for the time,
// and falls back to DEFAULT_DURATION when the duration can't be read and
// drops an unknown type (noting both in warnings).
function normalizeCards(cards, starttimeIso, warn, language) {
  return cards
    .filter((card) => card && typeof card === "object")
    .map((card, index) => {
      const { title, name, duration, minutes, ...rest } = card;
      // An empty topic is a legitimate "Untitled" card, so only a missing
      // one falls back to an alias or a placeholder.
      const topic = [card.topic, title, name].find(
        (candidate) => typeof candidate === "string"
      );
      const rawTime = [card.time, duration, minutes].find(
        (candidate) => candidate !== undefined && candidate !== null
      );
      const id =
        typeof card.id === "string" || typeof card.id === "number"
          ? card.id
          : `${starttimeIso}-${index}`;
      const label =
        topic ?? translate(language, "card.placeholderName", { number: index + 1 });
      let time = parseDuration(rawTime);
      if (!time) {
        time = DEFAULT_DURATION;
        if (rawTime === undefined || rawTime === null || rawTime === "") {
          warn("noDuration", { topic: label });
        } else {
          warn("badDuration", { duration: rawTime, topic: label });
        }
      }
      // Assigned in place, so a card that is already fine keeps its key
      // order and the link counts as unchanged.
      const result = { ...rest, id, topic: label, time };
      ["owner", "note"].forEach((key) => {
        if (typeof card[key] === "string" && card[key].trim()) {
          result[key] = card[key].trim();
        } else {
          delete result[key];
        }
      });
      if (card.type !== undefined && card.type !== null && card.type !== "") {
        result.type = parseCardType(card.type);
        if (!result.type) {
          warn("unknownType", { type: card.type, topic: label });
        }
      }
      if (!result.type) {
        delete result.type;
      }
      return result;
    });
}

// Takes the query string of the current location and returns
// { search, warnings }: the canonical query string plus a note (in the
// given language) for every part of the link that couldn't be read and
// was replaced by a default.
// Returns null if the link is already canonical, so callers only redirect
// when something actually changes.
export function normalizeSearch(rawSearch, now = moment(), language = "en") {
  const search = repairQuery(rawSearch);
  const queryRepaired = search !== rawSearch.replace(/^\?/, "");
  const query = new URLSearchParams(search);
  const hasStart = query.has("start");
  const hasPlan = query.has("plan");
  const givenStart = hasStart ? query.get("start") : query.get("starttime");
  const startIsCanonical =
    !hasStart &&
    givenStart !== null &&
    moment(givenStart, moment.ISO_8601, true).isValid();
  const rawStart =
    givenStart === null || startIsCanonical
      ? givenStart
      : stripTrailingJunk(givenStart);

  const warnings = [];
  const warn = (key, params) =>
    warnings.push(translate(language, `linkNotice.${key}`, params));

  // A plan without any start time starts right away, like a fresh board.
  let starttimeIso = startIsCanonical ? rawStart : null;
  if (!startIsCanonical && (rawStart !== null || hasPlan)) {
    let start = rawStart === null ? null : parseStart(rawStart, now);
    if (!start) {
      start = now.clone();
      if (rawStart !== null && rawStart.trim()) {
        warn("badStart", { start: rawStart });
      }
    }
    starttimeIso = start.toISOString();
  }

  const rawAgenda = query.get("agenda");
  const { cards: parsedAgenda, repaired: agendaRepaired } =
    rawAgenda === null
      ? { cards: null, repaired: false }
      : parseAgendaJson(rawAgenda);
  let cards = parsedAgenda;
  if (hasPlan) {
    cards = parsePlan(cleanPlan(decodeLenient(rawParam(search, "plan"))), warn);
  } else if (rawAgenda !== null && parsedAgenda === null) {
    cards = [];
    warn("badCards");
  }
  const agendaJson =
    cards === null
      ? null
      : JSON.stringify(
          normalizeCards(
            cards,
            starttimeIso || now.toISOString(),
            warn,
            language
          )
        );

  const agendaChanged =
    hasPlan ||
    agendaRepaired ||
    (rawAgenda !== null && JSON.stringify(parsedAgenda) !== agendaJson);
  const startChanged = hasStart || (rawStart !== null && !startIsCanonical);
  if (!startChanged && !agendaChanged && !queryRepaired) {
    return null;
  }

  // Encoded like pathHelper's concatParams (which can't be imported here
  // without pulling react-router into the unit tests).
  const params = [
    ["starttime", starttimeIso],
    ["agenda", agendaJson !== "[]" ? agendaJson : null],
  ];
  query.forEach((value, key) => {
    if (!["start", "starttime", "plan", "agenda"].includes(key)) {
      params.push([key, value]);
    }
  });
  const canonical = params
    .filter(([, value]) => value)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join("&");
  return { search: canonical, warnings };
}
